import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  HEAT_MAP_TITLE, HEAT_MAP_EMPTY, HEAT_MAP_LOADING, HEAT_MAP_LOAD_ERROR, HEAT_MAP_BREAKDOWN,
  HEAT_MAP_JOB_LOCATION, heatMapWithin, heatMapWorkersLabel,
} from '../utils/heatMapStrings';

const COLORS = {
  navy: '#0F1E2E',
  navyLight: '#1a3048',
  navyMid: '#1E3048',
  orange: '#E8590C',
  text: '#e0eaf2',
  textMuted: '#8aa0b0',
  textDim: '#b0c4d4',
  white: '#ffffff',
};

function WorkerHeatMap({ jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const mapRef = useRef(null);

  // Fetch worker availability data
  useEffect(() => {
    if (!jobId) return;
    const fns = getFunctions();
    const getWorkerAvailabilityMap = httpsCallable(fns, 'getWorkerAvailabilityMap');
    getWorkerAvailabilityMap({ jobId })
      .then((result) => {
        setData(result.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || HEAT_MAP_LOAD_ERROR);
        setLoading(false);
      });
  }, [jobId]);

  // Initialize Leaflet map once data is available
  useEffect(() => {
    // Guard: when areas is empty the map container isn't in the DOM,
    // so skip Leaflet init entirely to avoid 'Map container not found' errors.
    if (!data || !Array.isArray(data.areas) || data.areas.length === 0) return;

    const L = window.L;
    if (!L) return;

    // Destroy previous map instance before re-initializing
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const map = L.map('worker-map', {
      center: [data.jobLocation.lat, data.jobLocation.lng],
      zoom: 10,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);

    // Job location marker with popup
    L.marker([data.jobLocation.lat, data.jobLocation.lng])
      .addTo(map)
      .bindPopup(`<b>${data.jobLocation.city}, ${data.jobLocation.state}</b><br>${HEAT_MAP_JOB_LOCATION}`)
      .openPopup();

    const maxWorkers = Math.max(...data.areas.map((a) => a.availableWorkers), 1);

    data.areas.forEach((area) => {
      const radius = Math.max(800, area.availableWorkers * 200);
      const fillOpacity = 0.2 + (area.availableWorkers / maxWorkers) * 0.4;
      // Prompt #498: banded label ("5-9 workers"); a response without a band shows the number.
      const workerLabel = heatMapWorkersLabel(area.band, area.availableWorkers);

      const circle = L.circle([area.lat, area.lng], {
        radius,
        color: '#F97316',
        fillColor: '#F97316',
        fillOpacity,
        weight: 2,
        opacity: 0.85,
      }).addTo(map);

      circle.bindPopup(`<b>${area.city}</b>: ${workerLabel}`);
      // Permanent label showing worker count at circle center
      circle.bindTooltip(
        `<span style="font-weight:700;font-size:12px;color:#0F1E2E;">${area.band || area.availableWorkers}</span>`,
        { permanent: true, direction: 'center', offset: [0, 0] },
      );
      circle.on('mouseover', () => circle.openPopup());
      circle.on('click', () => circle.openPopup());
    });

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [data]);

  if (loading) {
    return (
      <div style={{
        backgroundColor: COLORS.navyMid,
        borderRadius: '12px',
        padding: '20px',
        color: COLORS.textMuted,
        fontSize: '13px',
        textAlign: 'center',
        marginBottom: '1.25rem',
      }}>
        {HEAT_MAP_LOADING}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: COLORS.navyMid,
        borderRadius: '12px',
        padding: '16px 20px',
        color: '#ef4444',
        fontSize: '13px',
        marginBottom: '1.25rem',
      }}>
        {error}
      </div>
    );
  }

  // D5 rebuild (Prompt #498): only areas with 5+ available workers are ever
  // returned, as bands ("5-9"); there is no remainder row any more. So an empty
  // areas list (including any non-active job) shows the explicit empty state,
  // which never reads as "no workers on the platform". Older responses without
  // band fields still render, showing plain numbers.
  const areas = data?.areas ?? [];
  if (!data || areas.length === 0) {
    return (
      <div
        data-testid="heat-map-empty"
        style={{
          backgroundColor: COLORS.navyMid,
          borderRadius: '12px',
          padding: '16px 20px',
          color: COLORS.textMuted,
          fontSize: '13px',
          marginBottom: '1.25rem',
        }}
      >
        <div style={{ fontSize: '15px', fontWeight: '600', color: COLORS.white, marginBottom: '6px' }}>
          {HEAT_MAP_TITLE}
        </div>
        {HEAT_MAP_EMPTY}
      </div>
    );
  }

  const maxWorkers = Math.max(...areas.map((a) => a.availableWorkers), 1);
  const totalLabel = data.totalBand || String(data.totalAvailable ?? 0);

  return (
    <div style={{
      backgroundColor: COLORS.navyMid,
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '1.25rem',
      color: COLORS.text,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '15px', fontWeight: '600', color: COLORS.white }}>
          {HEAT_MAP_TITLE}
        </div>
        <div data-testid="heat-map-total" style={{ fontSize: '24px', fontWeight: '700', color: COLORS.orange, lineHeight: 1 }}>
          {totalLabel}
        </div>
        <div style={{ fontSize: '12px', color: COLORS.textMuted }}>
          {heatMapWithin(data.searchRadiusMiles, data.jobLocation.city)}
        </div>
      </div>

      {/* Leaflet map */}
      <div
        id="worker-map"
        style={{
          height: '280px',
          width: '100%',
          borderRadius: '8px',
          marginBottom: '16px',
          overflow: 'hidden',
        }}
      />

      {/* Collapsible bar chart */}
      <div>
        {/* Toggle header */}
        <div
          onClick={() => setBreakdownOpen((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '6px 0',
            userSelect: 'none',
          }}
        >
          <span style={{ fontSize: '12px', fontWeight: '600', color: COLORS.textMuted, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            {HEAT_MAP_BREAKDOWN}
          </span>
          <span style={{
            fontSize: '12px',
            color: COLORS.textMuted,
            transition: 'transform 0.25s ease',
            display: 'inline-block',
            transform: breakdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            ▼
          </span>
        </div>

        {/* Collapsible content */}
        <div style={{
          maxHeight: breakdownOpen ? '800px' : '0px',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '10px' }}>
            {areas.map((area) => {
              const barPct = maxWorkers > 0 ? area.availableWorkers / maxWorkers : 0;
              const alpha = 0.3 + barPct * 0.7;

              return (
                <div key={area.city} data-testid="heat-map-area-row">
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: '5px',
                  }}>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: COLORS.text }}>
                      {area.city}
                    </span>
                    <span style={{ fontSize: '12px', color: COLORS.textMuted, whiteSpace: 'nowrap' }}>
                      {heatMapWorkersLabel(area.band, area.availableWorkers)} · {area.distanceMiles} mi
                    </span>
                  </div>
                  <div style={{
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: COLORS.navyLight,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${barPct * 100}%`,
                      borderRadius: '4px',
                      backgroundColor: `rgba(232, 89, 12, ${alpha})`,
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkerHeatMap;
